import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import {
  DashboardHeroMetric,
  DashboardMiniStat,
  DashboardProgressRow,
  DashboardAlertBanner,
  DashboardTrendBars,
  DashboardQuickAction,
  DashboardResourceCard,
  DashboardPanel,
  DashboardPageHeader,
  buildCaseDistributionBars,
  formatMetricValue,
} from "../../../shared/components/dashboard/DashboardBento";
import { useTranslation } from "../../../i18n";
import {
  fetchAdminDeletionCaseStats,
  fetchAdminUserStats,
} from "../adminApi";
import { useCurrentUser, getUserFirstName } from "../../../shared/components/UserProfileBadge";

const BASE = "/dashboard/admin";

const AdminDashboardHome = () => {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const firstName = getUserFirstName(currentUser);
  const [caseStats, setCaseStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [cases, users] = await Promise.all([
        fetchAdminDeletionCaseStats(),
        fetchAdminUserStats(),
      ]);
      setCaseStats(cases);
      setUserStats(users);
    } catch {
      setCaseStats(null);
      setUserStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const activeCases = (caseStats?.pending ?? 0) + (caseStats?.inProgress ?? 0);
  const paid = userStats?.paid ?? 0;
  const unpaid = userStats?.unpaid ?? 0;
  const accountTotal = paid + unpaid;
  const paidPercent = accountTotal > 0 ? Math.round((paid / accountTotal) * 100) : 0;
  const unpaidPercent = accountTotal > 0 ? 100 - paidPercent : 0;

  const goToCases = (filter = {}) => {
    navigate(`${BASE}/deletion-cases`, { state: filter });
  };

  const quickActions = useMemo(
    () => [
      {
        title: t("adminHome.actionCases"),
        description: t("adminHome.actionCasesDesc"),
        to: `${BASE}/deletion-cases`,
        icon: "folder_managed",
        iconTone: "primary",
      },
      {
        title: t("adminHome.actionUsers"),
        description: t("adminHome.actionUsersDesc"),
        to: `${BASE}/users`,
        icon: "group",
        iconTone: "secondary",
      },
      {
        title: t("adminHome.actionAlerts"),
        description: t("adminHome.actionAlertsDesc"),
        to: `${BASE}/email-alerts`,
        icon: "notifications",
        iconTone: "yellow",
      },
      {
        title: t("adminHome.actionDocs"),
        description: t("adminHome.actionDocsDesc"),
        to: `${BASE}/documentation`,
        icon: "description",
        iconTone: "primary",
      },
    ],
    [t]
  );

  const resourceLinks = useMemo(
    () => [
      { to: `${BASE}/documentation`, label: t("dashboardBento.adminResource1"), icon: "policy" },
      { to: `${BASE}/users`, label: t("dashboardBento.adminResource2"), icon: "group" },
      { to: `${BASE}/deletion-cases`, label: t("dashboardBento.adminResource3"), icon: "gavel" },
    ],
    [t]
  );

  const caseTrendBars = useMemo(
    () => buildCaseDistributionBars(caseStats, t),
    [caseStats, t]
  );

  return (
    <div className="bg-background font-body-md text-on-surface antialiased">
      <div className="mx-auto min-h-screen max-w-container-max px-margin-mobile py-stack-lg md:px-margin-desktop">
        <DashboardPageHeader
          eyebrow={t("adminHome.platformAdmin")}
          title={
            firstName
              ? t("home.greeting", { name: firstName })
              : t("adminHome.title")
          }
          subtitle={t("dashboardBento.adminHeroSubtitle")}
          actions={
            <span className="inline-flex items-center gap-2 rounded-full border border-outline-variant/40 bg-surface-container-lowest px-4 py-2 font-label-sm font-medium text-on-surface-variant">
              <MaterialIcon name="monitoring" className="text-[16px] text-secondary" />
              {t("dashboardBento.last30Days")}
            </span>
          }
        />

        <div className="mb-stack-lg grid grid-cols-1 gap-stack-md md:grid-cols-3">
          <DashboardHeroMetric
            label={t("adminHome.activeCasesLabel")}
            value={formatMetricValue(activeCases, locale)}
            footnote={t("adminHome.activeCasesHint")}
            icon="gavel"
            iconTone="secondary"
            borderClass="border-secondary"
            trendLabel={
              (caseStats?.newThisWeek ?? 0) > 0
                ? t("dashboardBento.newThisWeekShort", { count: caseStats.newThisWeek })
                : null
            }
            trendIcon="trending_up"
            loading={loading}
            onClick={() => navigate(`${BASE}/deletion-cases`)}
          />
          <DashboardHeroMetric
            label={t("adminUserStats.total")}
            value={formatMetricValue(userStats?.total ?? 0, locale)}
            footnote={t("adminUserStats.totalHint")}
            icon="group"
            iconTone="primary"
            borderClass="border-primary"
            trendLabel={
              (userStats?.newThisWeek ?? 0) > 0
                ? `+${formatMetricValue(userStats.newThisWeek, locale)}`
                : null
            }
            trendIcon="person_add"
            loading={loading}
            onClick={() => navigate(`${BASE}/users`)}
          />
          <DashboardHeroMetric
            label={t("adminUserStats.new")}
            value={formatMetricValue(userStats?.newThisWeek ?? 0, locale)}
            footnote={t("dashboardBento.adminNewFootnote")}
            icon="bolt"
            iconTone="yellow"
            borderClass="border-yellow-500"
            trendLabel={(userStats?.newThisWeek ?? 0) > 0 ? t("dashboardBento.peak") : null}
            trendIcon="trending_up"
            accent
            loading={loading}
            onClick={() => navigate(`${BASE}/users`)}
          />
        </div>

        <div className="mb-stack-lg grid grid-cols-1 gap-stack-lg lg:grid-cols-12">
          <div className="lg:col-span-4">
            <DashboardPanel title={t("dashboardBento.userOverview")} icon="group">
              <div className="space-y-4">
                <DashboardProgressRow
                  label={t("adminUserStats.paid")}
                  value={formatMetricValue(paid, locale)}
                  percent={paidPercent}
                  barClassName="bg-secondary"
                  icon="verified"
                />
                <DashboardProgressRow
                  label={t("adminUserStats.unpaid")}
                  value={formatMetricValue(unpaid, locale)}
                  percent={unpaidPercent}
                  barClassName="bg-outline"
                  icon="hourglass_empty"
                />
              </div>
              {(userStats?.newThisWeek ?? 0) > 0 ? (
                <DashboardAlertBanner
                  icon="trending_up"
                  title={t("dashboardBento.growthAlert")}
                  message={t("dashboardBento.growthAlertMsg", {
                    count: userStats.newThisWeek,
                  })}
                />
              ) : null}
            </DashboardPanel>
          </div>

          <div className="lg:col-span-8">
            <DashboardPanel
              title={t("adminHome.analyticsTitle")}
              icon="analytics"
              badge={t("dashboardBento.last30Days")}
            >
              <div className="mb-stack-lg grid grid-cols-2 gap-4 sm:grid-cols-3">
                <DashboardMiniStat
                  label={t("adminStats.total")}
                  value={caseStats?.total}
                  locale={locale}
                  loading={loading}
                  icon="assignment"
                  onClick={() => goToCases()}
                />
                <DashboardMiniStat
                  label={t("adminStats.new")}
                  value={caseStats?.newThisWeek}
                  locale={locale}
                  loading={loading}
                  icon="today"
                  iconTone="yellow"
                  dot
                  onClick={() => goToCases({ createdWithinDays: 7 })}
                />
                <DashboardMiniStat
                  label={t("adminStats.pending")}
                  value={caseStats?.pending}
                  locale={locale}
                  loading={loading}
                  icon="schedule"
                  onClick={() => goToCases({ statusGroup: "pending" })}
                />
                <DashboardMiniStat
                  label={t("adminStats.inProgress")}
                  value={caseStats?.inProgress}
                  locale={locale}
                  loading={loading}
                  icon="sync"
                  iconTone="secondary"
                  onClick={() => goToCases({ statusGroup: "in_progress" })}
                />
                <DashboardMiniStat
                  label={t("adminStats.completed")}
                  value={caseStats?.completed}
                  locale={locale}
                  loading={loading}
                  icon="check_circle"
                  highlight
                  onClick={() => goToCases({ statusGroup: "completed" })}
                />
                <DashboardMiniStat
                  label={t("adminStats.rejected")}
                  value={caseStats?.rejected}
                  locale={locale}
                  loading={loading}
                  icon="cancel"
                  errorTone
                  onClick={() => goToCases({ statusGroup: "rejected" })}
                />
              </div>

              <DashboardTrendBars
                title={t("dashboardBento.caseDistribution")}
                bars={caseTrendBars}
                loading={loading && !caseStats}
                locale={locale}
                emptyMessage={t("dashboardBento.chartEmpty")}
              />
            </DashboardPanel>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-stack-lg lg:grid-cols-12">
          <div className="lg:col-span-7">
            <h3 className="mb-stack-md inline-flex items-center gap-2 font-headline-md text-headline-md text-primary">
              <MaterialIcon name="bolt" className="text-[20px] text-secondary" />
              {t("home.quickActions")}
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {quickActions.map((action) => (
                <DashboardQuickAction key={action.to} {...action} />
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <h3 className="mb-stack-md inline-flex items-center gap-2 font-headline-md text-headline-md text-primary">
              <MaterialIcon name="menu_book" className="text-[20px] text-secondary" />
              {t("dashboardBento.resources")}
            </h3>
            <DashboardResourceCard
              title={t("adminHome.opsGuideTitle")}
              description={t("adminHome.opsGuideDesc")}
              links={resourceLinks}
              icon="description"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardHome;
